import {TranslateService} from "@ngx-translate/core";
import { LanguageService } from "@app/services/language.service";
import html2canvas from 'html2canvas';
import {PDFDocument, PDFFont, PDFPage, StandardFonts, rgb} from 'pdf-lib';
import { MessageService, TreeNode } from 'primeng/api';
import { PdfPagination } from './page-break-layout';

/**
 * Format a number according to the current locale
 * @param num - Number or string to format
 * @param languageService - LanguageService instance to get current locale
 * @param options - Optional Intl.NumberFormatOptions for custom formatting
 * @returns Formatted number string
 */
export function formatNumber(
  num: number | string,
  languageService: LanguageService,
  options?: Intl.NumberFormatOptions
): string {
  const numValue = typeof num === 'string' ? parseFloat(num) : num;

  if (isNaN(numValue)) {
    return '';
  }

  // Get locale from centralized LanguageService
  const locale = languageService.getLocaleId();

  // Default options: 2 decimal places
  const finalOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  };

  const formatter = new Intl.NumberFormat(locale, finalOptions);
  return formatter.format(numValue);
}

/**
 * Format currency according to the current locale
 * @param num - Number or string to format
 * @param languageService - LanguageService instance to get current locale
 * @param currency - Currency code (default: 'EUR')
 * @returns Formatted currency string
 */
export function formatCurrency(
  num: number | string,
  languageService: LanguageService,
  currency: string = 'EUR'
): string {
  const numValue = typeof num === 'string' ? parseFloat(num) : num;

  if (isNaN(numValue)) {
    return '';
  }

  // Get locale from centralized LanguageService
  const locale = languageService.getLocaleId();

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter.format(numValue);
}

/**
 * Round a monetary value to whole cents (2 decimal places).
 *
 * Each period's amount is a real euro amount billed at cent precision, so it
 * must be rounded before being summed. Summing the raw (unrounded) products of
 * hours * price and rounding only at display time produces off-by-one-cent
 * totals (e.g. 7.8h * 88.84 = 692.952 per quarter: 4 * 692.952 = 2771.808 ->
 * "2.771,81 €" instead of the correct 4 * 692.95 = "2.771,80 €").
 *
 * @param value - Monetary value to round
 * @returns Value rounded to 2 decimal places (0 for null/NaN input)
 */
export function roundToCents(value: number | string | null | undefined): number {
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (isNaN(num)) {
    return 0;
  }
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

export function formatDate(date: Date, locale?: String, time?: boolean): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  if (year == '1970') {
    return '';
  }
  let seperator = '.';
  if (locale !== 'de') {
    seperator = '/';
  }
  let rDate = `${day}${seperator}${month}${seperator}${year}`;
  if (time) {
    rDate += ` ${hours}:${minutes}`
  }
  return rDate;
}

/**
 * Formats a date as dd.MM.yyyy, which is what the date controls emit and what the
 * API parses. Sending a Date instead would be serialized as a UTC timestamp, which
 * can land on the previous day.
 */
export function toControlDate(date: Date): string {
  const pad = (value: number) => `${value}`.padStart(2, '0');

  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

export interface OverdueStatus {
  /** Date is today or already in the past. */
  isOverdue: boolean;
  /** Date is exactly today. */
  isToday: boolean;
}

/**
 * Parse a backend date string into a local Date at midnight.
 *
 * The API is not consistent about the format, so this supports both day-first
 * values ("01.05.2026", "01-05-2026", "01/05/2026") and ISO year-first values
 * ("2026-05-01", "2026/05/01", and ISO datetimes like "2026-05-01T00:00:00Z").
 * Returns null when the value can't be parsed. The regex mirrors FormatDatePipe
 * so the compared date always matches what is displayed.
 */
export function parseBackendDate(value: string | Date | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  const parts = value.match(/(\d{1,2})[-./]?(\d{1,2})[-./]?(\d{4})|(\d{4})[-./]?(\d{1,2})[-./]?(\d{1,2})/);
  if (parts) {
    const year = parseInt(parts[3] || parts[4]);
    const month = parseInt(parts[2] || parts[5]) - 1; // Months are zero-based in JavaScript
    const day = parseInt(parts[1] || parts[6]);

    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
      return new Date(year, month, day);
    }
  }

  return null;
}

/**
 * Compare a backend date against today so it can be highlighted the same way as
 * the backend `is_overdue` / `is_overdue_today` flags: overdue once the date is
 * reached, and flagged as "today" on the due date itself.
 */
export function getOverdueStatus(value: string | Date | null | undefined): OverdueStatus {
  const date = parseBackendDate(value);
  if (!date) {
    return { isOverdue: false, isToday: false };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  const time = date.getTime();
  const todayTime = today.getTime();

  return {
    isOverdue: time <= todayTime,
    isToday: time === todayTime,
  };
}

export function deleteNullValueKeys(obj: Object) {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (obj[key] === null || obj[key] === '' || obj[key] === undefined) {
        delete obj[key];
      } else if (typeof obj[key] === 'object' && !(obj[key] instanceof Date)) {
        deleteNullValueKeys(obj[key]); // Recursively check nested objects
        // Only delete if ALL properties in the nested object are null/empty/undefined
        const hasValidValues = Object.values(obj[key]).some(value => 
          value !== null && 
          value !== '' && 
          value !== undefined && 
          value !== false
        );
        if (!hasValidValues) {
          delete obj[key];
        }
      }
    }
  }
  return obj;
}

export function saveAsExcelFile(buffer: any, fileName: string, extension: string): void {
  const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const EXCEL_EXTENSION = extension//'.xlsx';
  const data: Blob = new Blob([buffer as unknown as BlobPart], {
    type: EXCEL_TYPE
  });
  const saveAs = require('file-saver').saveAs;
  saveAs(data, fileName + '(' + new Date() + ')' + EXCEL_EXTENSION);
}

export function getEmptyIfNull(value: any) {
  if (value == null) {
    return '';
  }
  return value;
}

export function convertFileToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve) => {
    if (!file) {
      resolve('');
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = reader.result as string;
      resolve(base64String);
    };
  });
}

export function generateExportFileName() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
}

function formatDateForExportFileName(date: Date | string | null | undefined): string {
  if (!date) {
    return 'unknown_date';
  }
  const parsedDate = date instanceof Date ? date : new Date(date);
  if (isNaN(parsedDate.getTime())) {
    return 'unknown_date';
  }
  const day = String(parsedDate.getDate()).padStart(2, '0');
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const year = parsedDate.getFullYear();
  return `${day}${month}${year}`;
}

function sanitizeExportFileNameSegment(value: string): string {
  return value.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
}

function getInjuredPersonNameForExport(profile: {
  injured_visitor?: boolean;
  injured_user_name?: string;
  injured_person?: { salutation?: string; firstname?: string; name?: string };
  accident_injured_visitor_attributes?: { first_name?: string; last_name?: string };
}): string {
  if (profile?.injured_visitor) {
    const firstName = profile?.accident_injured_visitor_attributes?.first_name || '';
    const lastName = profile?.accident_injured_visitor_attributes?.last_name || '';
    return `${firstName} ${lastName}`.trim() || profile?.injured_user_name || 'Unknown_Visitor';
  } else if (profile?.injured_person) {
    const salutation = profile?.injured_person?.salutation || '';
    const firstName = profile?.injured_person?.firstname || '';
    const lastName = profile?.injured_person?.name || '';
    return `${salutation} ${firstName} ${lastName}`.trim() || 'Unknown_Employee';
  }
  return 'Unknown_Person';
}

export function generateAccidentReportPdfFileName(
  profile: {
    accident_type?: string;
    accident_time?: string | Date | null;
    injured_visitor?: boolean;
    injured_user_name?: string;
    injured_person?: { salutation?: string; firstname?: string; name?: string };
    accident_injured_visitor_attributes?: { first_name?: string; last_name?: string };
  },
  reportTitle: string,
): string {
  const accidentDate = formatDateForExportFileName(profile.accident_time);
  const todayDate = formatDateForExportFileName(new Date());
  const hasInjuredPerson = !!(profile?.injured_person || profile?.accident_injured_visitor_attributes || profile?.injured_user_name);
  const injuredPersonSegment = hasInjuredPerson
    ? `${sanitizeExportFileNameSegment(getInjuredPersonNameForExport(profile))}_`
    : '';
  const reportTitleSegment = sanitizeExportFileNameSegment(reportTitle);
  return `${reportTitleSegment}_${injuredPersonSegment}${accidentDate}_${todayDate}.pdf`;
}

export function stringToDate(value: string | string[] | Date | null): Date[] | Date | null {
  if (value instanceof Date) {
    return value; // If value is already a Date object, return it
  }

  if (typeof value === 'string') {
    return parseDate(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => parseDate(item)).filter((date) => date !== null) as Date[];
  }

  return null;
}

function parseDate(dateValue: string): Date | null {
  const parts = dateValue.match(/(\d{1,2})[-.]?(\d{1,2})[-.]?(\d{4})|(\d{4})[-.]?(\d{1,2})[-.]?(\d{1,2})/);
  if (parts) {
    const year = parseInt(parts[3] || parts[4]);
    const month = parseInt(parts[2] || parts[5]) - 1; // Months are zero-based in JavaScript
    const day = parseInt(parts[1] || parts[6]);

    // Check if the extracted values form a valid date
    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
      return new Date(year, month, day);
    }
  }

  return null;
}

export function translateAndReplace(translateService: TranslateService, key: string, options: any = null): string {
  let translation = translateService.instant(key, options);

  for (const placeholder in options) {
    if (Object.prototype.hasOwnProperty.call(options, placeholder)) {
      const placeholderValue = options[placeholder];
      const placeholderRegex = new RegExp(`%{${placeholder}}`, 'g');
      translation = translation.replace(placeholderRegex, placeholderValue === null ? '' : placeholderValue);
    }
  }

  return translation;
}

// export function convertFileToBase64(file: File): Promise<string> {
export async function htmlToPdfFile(divElement: HTMLElement): Promise<File> {
  const scale = 2;
  divElement.querySelectorAll('.editable-click').forEach(el => el.classList.remove('editable-click'));

  const canvas = await html2canvas(divElement, { scale });
  const pdfDoc = await PDFDocument.create();

  const pageWidth = 595.28; // A4 width in pt
  const pageHeight = 841.89; // A4 height in pt
  const imgWidth = pageWidth;
  const bottomSpace = 40;
  let topSpace = 0;
  let yOffset = 0;

  const canvasScaleFactor = pageWidth / canvas.width;
  const availableHeightInCanvas = (pageHeight - topSpace - bottomSpace) / canvasScaleFactor;

  const sectionBounds = Array.from(divElement.querySelectorAll('.section')).map(el => {
    const rect = (el as HTMLElement).getBoundingClientRect();
    const containerRect = divElement.getBoundingClientRect();
    const top = Math.round((rect.top - containerRect.top) * scale);
    const height = Math.round(rect.height * scale);
    return { top, height };
  });

  const containerRect = divElement.getBoundingClientRect();
  const content = divElement.querySelector('#print-area-container') as HTMLElement;
  const contentRect = content.getBoundingClientRect();
  const contentLeft = Math.round((contentRect.left - containerRect.left) * scale);
  const contentRight = Math.round((containerRect.right - contentRect.right) * scale);

  while (yOffset < canvas.height) {
    const page = pdfDoc.addPage([pageWidth, pageHeight]);
    if (pdfDoc.getPageCount() > 1) {
      topSpace = 20;
    }

    let drawHeight = Math.min(availableHeightInCanvas, canvas.height - yOffset);

    const breakingSection = sectionBounds.find(({ top, height }) =>
      top >= yOffset &&
      top < yOffset + drawHeight - 10 &&
      top + height > yOffset + drawHeight
    );

    const addBreakLine = !!breakingSection;

    if (breakingSection) {
      drawHeight = breakingSection.top - yOffset;
    }

    if (drawHeight <= 0) {
      drawHeight = availableHeightInCanvas;
    }

    const canvasPage = document.createElement('canvas');
    canvasPage.width = canvas.width;
    canvasPage.height = drawHeight;
    const ctx = canvasPage.getContext('2d');

    ctx?.drawImage(
      canvas,
      0,
      yOffset,
      canvas.width,
      drawHeight,
      0,
      0,
      canvas.width,
      drawHeight
    );
    
    if (ctx && yOffset > 0) {
      ctx.fillStyle = 'white';
    
      // Cover top leftovers
      ctx.fillRect(contentLeft - 1, 0, canvasPage.width - contentLeft - contentRight + 1, 5);
    }
    
    
    if (addBreakLine && ctx) {
      ctx.beginPath();
      ctx.moveTo(contentLeft, canvasPage.height - 1);
      ctx.lineTo(canvasPage.width - contentRight, canvasPage.height - 1);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = '#000000';
      ctx.stroke();
    }

    const imgData = canvasPage.toDataURL('image/png');
    const imageBytes = await fetch(imgData).then(res => res.arrayBuffer());
    const pdfImage = await pdfDoc.embedPng(imageBytes);

    page.drawImage(pdfImage, {
      x: 0,
      y: pageHeight - topSpace - drawHeight * canvasScaleFactor,
      width: imgWidth,
      height: drawHeight * canvasScaleFactor,
    });

    yOffset += drawHeight;
  }

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' });
  const file = new File([blob], 'document.pdf', { type: 'application/pdf' });

  return file;
}

export async function htmlToPdfFileWithLoadingImages(
  divElement: HTMLElement,
  pageStyle?: { border?: string },
  pageNumberText?: string,
  footerText?: string,
  footerDate?: string,
  pagination?: PdfPagination
): Promise<File> {
  // Create a blank PDF doc
  const pdfDoc = await PDFDocument.create();

  // Typical A4 size in points (~210mm x 297mm):
  const pageWidth = 595.28;
  const pageHeight = 841.89;

  // Keep a base margin from the page edge to the outer border edge
  const baseMargin = 20;

  /**
   * Convert a named CSS color to hex if possible.
   */
  function namedColorToHex(color: string): string | null {
    const colorMap: Record<string, string> = {
      black: "#000000",
      orange: "#ffa500",
      yellow: "#ffff00",
      green: "#00ff00",
      blue: "#0000ff",
      // Add more if needed
    };
    const lower = color.toLowerCase();
    return colorMap[lower] || null;
  }

  /**
   * Helper to embed an image from a dataUrl into the PDF.
   */
  async function embedImage(pdfDoc: PDFDocument, dataUrl: string) {
    const mime = dataUrl.split(";")[0].split(":")[1]; // e.g. "image/png" or "image/jpeg"
    const arrayBuffer = await fetch(dataUrl).then((res) => res.arrayBuffer());
    if (mime === "image/png") {
      return pdfDoc.embedPng(arrayBuffer);
    } else if (mime === "image/jpeg") {
      return pdfDoc.embedJpg(arrayBuffer);
    } else {
      throw new Error(`Unsupported image mime type: ${mime}`);
    }
  }

  /**
   * Converts a color string (hex or named) to {r,g,b} in [0..1].
   */
  function hexToRgbFraction(colorStr: string) {
    if (!colorStr.startsWith("#")) {
      const hex = namedColorToHex(colorStr);
      if (hex) colorStr = hex;
    }
    const cleaned = colorStr.replace("#", "");
    if (cleaned.length === 3) {
      const r = parseInt(cleaned.charAt(0) + cleaned.charAt(0), 16);
      const g = parseInt(cleaned.charAt(1) + cleaned.charAt(1), 16);
      const b = parseInt(cleaned.charAt(2) + cleaned.charAt(2), 16);
      return { r: r / 255, g: g / 255, b: b / 255 };
    } else if (cleaned.length === 6) {
      const r = parseInt(cleaned.substring(0, 2), 16);
      const g = parseInt(cleaned.substring(2, 4), 16);
      const b = parseInt(cleaned.substring(4, 6), 16);
      return { r: r / 255, g: g / 255, b: b / 255 };
    }
    return null;
  }

  // Parse any border from pageStyle
  let borderThickness = 0;
  let borderRGB = { r: 0, g: 0, b: 0 };

  if (pageStyle?.border) {
    // e.g. "2px solid #ff0000"
    const [borderWidthStr = "4px", , borderColor = "#000"] = pageStyle.border.split(" ");
    borderThickness = parseFloat(borderWidthStr) || 0;
    const parsedColor = hexToRgbFraction(borderColor);
    if (parsedColor) {
      borderRGB = parsedColor;
    }
  }

  /**
   * Creates a new PDF page, optionally drawing a border.
   */
  function addNewPage() {
    const page = pdfDoc.addPage([pageWidth, pageHeight]);
    if (borderThickness > 0) {
      page.drawRectangle({
        x: baseMargin,
        y: baseMargin,
        width: pageWidth - 2 * baseMargin,
        height: pageHeight - 2 * baseMargin,
        borderWidth: borderThickness,
        borderColor: rgb(borderRGB.r, borderRGB.g, borderRGB.b),
      });
    }
    return page;
  }

  // The content should start inside the border
  const contentMargin = baseMargin + borderThickness / 2;

  // Start with the first page
  let currentPage = addNewPage();

  // Our vertical offset from the top for placing the next block
  let yOffset = pageHeight - contentMargin;

  // The content is drawn this wide on every page, and the page holds this much of it.
  const maxContentWidth = pageWidth - 2 * contentMargin;
  const contentHeightPerPage = pageHeight - 2 * contentMargin;

  // Capture the entire content area as one picture, then cut it into pages.
  const scale = 2;
  let pageCuts: { cuts: number[]; end: number } | null = null;
  const contentCanvas = await html2canvas(divElement, {
    scale,
    useCORS: true,
    windowWidth: divElement.scrollWidth,
    windowHeight: divElement.scrollHeight,
    // Runs on html2canvas's copy of the page, right before it is drawn, so the cuts
    // are worked out on exactly the layout that ends up in the picture.
    onclone: pagination ? (_clonedDocument, clonedElement) => {
      pagination.prepareClone?.(clonedElement);
      if (pagination.getCuts) {
        const widthPx = clonedElement.getBoundingClientRect().width;
        pageCuts = pagination.getCuts(clonedElement, contentHeightPerPage * widthPx / maxContentWidth);
      }
    } : undefined,
  });

  if (contentCanvas.width > 0 && contentCanvas.height > 0) {
    const ptPerCanvasPx = maxContentWidth / contentCanvas.width;

    // Page slices as [top, bottom] in canvas px.
    const slices: [number, number][] = [];
    if (pageCuts) {
      // Cut where the preview shows the page breaks. A slice taller than a page can only
      // come from broken input; it is split at the page height so no content is lost.
      const pagePx = Math.floor(contentHeightPerPage / ptPerCanvasPx);
      const end = Math.min(contentCanvas.height, Math.round(pageCuts.end * scale));
      const edges = [0];
      for (const y of pageCuts.cuts) {
        const row = Math.round(y * scale);
        if (row <= edges[edges.length - 1] || row >= end) continue;
        edges.push(cleanestCutRow(contentCanvas, row, edges[edges.length - 1], pagePx));
      }
      // Below the last line there is only empty space, so the last page never needs more than one page.
      edges.push(Math.min(end, edges[edges.length - 1] + pagePx));
      for (let i = 1; i < edges.length; i++) {
        let top = edges[i - 1];
        while (edges[i] - top > pagePx + 1) {
          slices.push([top, top + pagePx]);
          top += pagePx;
        }
        if (edges[i] - top >= 1) slices.push([top, edges[i]]);
      }
    } else {
      // Fixed-height pages. Leave a small buffer (~1 line of text) for html2canvas
      // rendering differences, so text near the page edge is less likely to be cut.
      const renderingBuffer = 12; // points
      const pagePx = (contentHeightPerPage - renderingBuffer) / ptPerCanvasPx;
      for (let top = 0; top < contentCanvas.height; top += pagePx) {
        slices.push([top, Math.min(top + pagePx, contentCanvas.height)]);
      }
    }

    for (let i = 0; i < slices.length; i++) {
      const [top, bottom] = slices[i];
      const sourceHeightPixels = bottom - top;
      const heightOnThisPage = sourceHeightPixels * ptPerCanvasPx;

      // Create a new canvas for this page slice
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = contentCanvas.width;
      sliceCanvas.height = Math.ceil(sourceHeightPixels);

      const sliceCtx = sliceCanvas.getContext('2d');
      if (sliceCtx) {
        sliceCtx.drawImage(
          contentCanvas,
          0, top,  // source x, y
          contentCanvas.width, sourceHeightPixels,  // source width, height
          0, 0,  // destination x, y
          contentCanvas.width, sourceHeightPixels   // destination width, height
        );

        // Convert slice to PDF image, drawn at the top of the page
        const sliceDataUrl = sliceCanvas.toDataURL();
        const slicePdfImage = await embedImage(pdfDoc, sliceDataUrl);

        currentPage.drawImage(slicePdfImage, {
          x: contentMargin,
          y: pageHeight - contentMargin - heightOnThisPage,
          width: maxContentWidth,
          height: heightOnThisPage,
        });
      }

      if (i < slices.length - 1) {
        currentPage = addNewPage();
      }
    }
  }

  // Embed a standard font for page numbering
  const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const allPages = pdfDoc.getPages();
  const totalPages = allPages.length;

  // Draw page numbers at the bottom center
  const currentDate = footerDate || new Date().toLocaleDateString();

  for (let i = 0; i < totalPages; i++) {
    const page = allPages[i];
    const { width } = page.getSize();
    const fontSize = 10;

    const dateText = currentDate;
    const centerText = footerText || '';
    const pageNumText = pageNumberText
      ? pageNumberText.replace('{{current}}', (i + 1).toString()).replace('{{total}}', totalPages.toString())
      : `Page ${i + 1} of ${totalPages}`;

    const dateTextWidth = helveticaFont.widthOfTextAtSize(dateText, fontSize);
    const centerTextWidth = helveticaFont.widthOfTextAtSize(centerText, fontSize);
    const pageNumTextWidth = helveticaFont.widthOfTextAtSize(pageNumText, fontSize);
    const y = baseMargin / 2;

    page.drawText(dateText, {
      x: baseMargin,
      y,
      font: helveticaFont,
      size: fontSize,
      color: rgb(0, 0, 0),
    });

    if (footerText) {
      page.drawText(centerText, {
        x: (width - centerTextWidth) / 2,
        y,
        font: helveticaFont,
        size: fontSize,
        color: rgb(0, 0, 0),
      });
    }

    page.drawText(pageNumText, {
      x: width - pageNumTextWidth - baseMargin,
      y,
      font: helveticaFont,
      size: fontSize,
      color: rgb(0, 0, 0),
    });
  }

  // Save as PDF
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes as unknown as BlobPart], { type: "application/pdf" });
  const file = new File([blob], "document.pdf", { type: "application/pdf" });

  return file;
}

/**
 * The pixel row near `row` where the fewest letters cross the cut. html2canvas draws text
 * about 1 CSS px lower than the browser, so where two lines touch, letter tails like the
 * bottom of a "g" reach past the cut and would show as dots at the top of the next page.
 * Rows close to `row` win ties. Never lets the page above grow past `pagePx`.
 */
function cleanestCutRow(canvas: HTMLCanvasElement, row: number, pageTop: number, pagePx: number): number {
  const from = Math.max(pageTop + 1, row - 2);
  const to = Math.min(canvas.height - 1, row + 4, pageTop + pagePx);
  if (from > row || row > to) return row;
  let pixels: Uint8ClampedArray;
  try {
    pixels = canvas.getContext('2d').getImageData(0, from - 1, canvas.width, to - from + 2).data;
  } catch {
    return row;
  }
  const ink = (r: number, x: number) => {
    const i = ((r - from + 1) * canvas.width + x) * 4;
    return pixels[i + 3] > 0 && (pixels[i] < 235 || pixels[i + 1] < 235 || pixels[i + 2] < 235);
  };
  let best = row;
  let bestScore = Infinity;
  for (let cut = from; cut <= to; cut++) {
    // Columns inked on both sides of the cut, plus a little for each row moved.
    let score = 2 * Math.abs(cut - row);
    for (let x = 0; x < canvas.width; x++) {
      if (ink(cut - 1, x) && ink(cut, x)) score++;
    }
    if (score < bestScore) {
      best = cut;
      bestScore = score;
    }
  }
  return best;
}

export function downloadFile(file: File, fileName: string): void {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(file);
  link.download = fileName;
  link.click();
}

export function downloadBase64AsPdfFile(base64: string, fileName: string) {
  const byteString = atob(base64.split(',')[1]);
  const byteNumbers = new Array(byteString.length);
  
  for (let i = 0; i < byteString.length; i++) {
    byteNumbers[i] = byteString.charCodeAt(i);
  }
  
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray as unknown as BlobPart], { type: 'application/pdf' });

  // Convert the Blob to a File
  const file = new File([blob], fileName, { type: 'application/pdf' });
  downloadFile(file, fileName);
}

//work in progress
export function htmlToPdfFileSearchable(divId: string): Promise<File> {
  return new Promise<File>(async (resolve, reject) => {
    const fontkit = require('fontkit'); //required for custom fonts
    const pdfDoc = await PDFDocument.create();
    pdfDoc.registerFontkit(fontkit);

    // Load the custom fonts
    const fontBytes = await fetch('/assets/fonts/roboto/Roboto-Regular.ttf').then(res => res.arrayBuffer());
    const customFont = await pdfDoc.embedFont(fontBytes);
    const boldFontBytes = await fetch('/assets/fonts/roboto/Roboto-Bold.ttf').then(res => res.arrayBuffer());
    const boldFont = await pdfDoc.embedFont(boldFontBytes);

    let page = pdfDoc.addPage([595.28, 841.89]); // A4 size in points
    let yPosition = page.getHeight() - 50; // Start at the top of the page
    const divElement = document.getElementById(divId);
    if (!divElement) {
      throw new Error(`Element with ID ${divId} not found.`);
    }

    renderHtmlToPdf(divElement, page, customFont, boldFont, yPosition, pdfDoc);

    const pdfBytes = await pdfDoc.save();

    const blob = new Blob([pdfBytes as unknown as BlobPart], {type: 'application/pdf'});
    const file = new File([blob], 'document.pdf', {type: 'application/pdf'});
    resolve(file);
  });
}

export function renderHtmlToPdf(
    element: HTMLElement,
    page: PDFPage,
    font: PDFFont,
    boldFont: PDFFont,
    yPosition: number,
    pdfDoc: PDFDocument
  ) {
    const childNodes = Array.from(element.childNodes);

    childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) {
          const styles = extractStyles(element, font, boldFont, page.getWidth());
          const lines = splitTextIntoLines(text, page.getWidth() - 100, styles.font, styles.fontSize);
          lines.forEach((line) => {
            if (yPosition < 50) {
              yPosition = page.getHeight() - 50;
              page = pdfDoc.addPage([595.28, 841.89]);
            }
            page.drawText(line, {
              x: styles.x,
              y: yPosition,
              size: styles.fontSize,
              font: styles.font,
              color: styles.color,
            });
            yPosition -= styles.lineHeight;
          });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;

        const styles = extractStyles(el, font, boldFont, page.getWidth());

        if (el.tagName === 'B' || el.tagName === 'STRONG') {
          renderHtmlToPdf(el, page, font, boldFont, yPosition, pdfDoc);
        } else if (el.tagName === 'P' || el.tagName === 'DIV') {
          yPosition = renderHtmlToPdf(el, page, font, boldFont, yPosition, pdfDoc);
        } else if (el.tagName === 'BR') {
          yPosition -= styles.lineHeight / 2; // Add a small space for line breaks
        } else if (el.tagName === 'TABLE') {
          // yPosition = this.renderTableToPdf(el, page, font, boldFont, yPosition, pdfDoc, styles);
        } else if (el.tagName === 'H1') {
          const headingText = el.textContent.trim();
          if (headingText) {
            const headingFontSize = styles.fontSize + 8;
            const textWidth = boldFont.widthOfTextAtSize(headingText, headingFontSize);
            const xPosition = page.getWidth() - 50 - textWidth; // Right justify the text
            page.drawText(headingText, {
              x: xPosition,
              y: yPosition,
              size: headingFontSize,
              font: boldFont,
              color: styles.color,
            });
            yPosition -= styles.lineHeight * 1.5;
          }
        }
      }
    });

    return yPosition;
  }
  export function extractStyles(element: HTMLElement, font: PDFFont, boldFont: PDFFont, pageWidth: number) {
    const styles: any = {
      fontSize: 12,
      font,
      color: rgb(0, 0, 0),
      lineHeight: 24,
      borderColor: rgb(0, 0, 0),
      borderWidth: 1,
      padding: 5,
      x: 50, // Default x position (left margin)
      y: 0,  // Default y position (to be adjusted dynamically)
      align: 'left', // Default alignment
    };

    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    if (style.fontSize) styles.fontSize = parseInt(style.fontSize) * 0.75;
    if (style.fontWeight === 'bold') styles.font = boldFont;
    if (style.color) {
      const [r, g, b] = hexToRgb(style.color);
      styles.color = rgb(r / 255, g / 255, b / 255);
    }
    if (style.lineHeight === 'normal') {
      styles.lineHeight = styles.fontSize * 1.2; // Assume "normal" is 1.2 times the font size
    } else {
      styles.lineHeight = parseInt(style.lineHeight);
    }

    if (style.borderColor) {
      const [r, g, b] = hexToRgb(style.borderColor);
      styles.borderColor = rgb(r / 255, g / 255, b / 255);
    }
    if (style.borderWidth) styles.borderWidth = parseInt(style.borderWidth);
    if (style.padding) styles.padding = parseInt(style.padding);

    // Extract x and y positions
    const elementWidth = rect.width;
    styles.x = rect.left;

    // Adjust for flex alignments
    if (style.textAlign === 'center') {
      styles.x = (pageWidth - elementWidth) / 2;
    } else if (style.textAlign === 'right' || style.justifyContent === 'end') {
      styles.x = pageWidth - elementWidth - 50; // 50 is a margin buffer
    }

    // Ensure the x value does not exceed page bounds
    if (styles.x < 50) styles.x = 50; // Ensure it doesn't go beyond the left margin
    if (styles.x + elementWidth > pageWidth - 50) {
      styles.x = pageWidth - elementWidth - 50; // Adjust to fit within the right margin
    }

    styles.y = rect.top;

    return styles;
  }

  export function hexToRgb(hex: string) {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => {
      return r + r + g + g + b + b;
    });
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
      : [0, 0, 0];
  }

  export function splitTextIntoLines(text: string, maxWidth: number, font: any, fontSize: number) {
    const words = text.split(' ');
    let lines = [];
    let line = '';

    words.forEach((word) => {
      const testLine = line + word + ' ';
      const testWidth = font.widthOfTextAtSize(testLine, fontSize);

      if (testWidth > maxWidth) {
        lines.push(line);
        line = word + ' ';
      } else {
        line = testLine;
      }
    });

    lines.push(line.trim());
    return lines;
  }

  export function sortTreeNodes(nodes: TreeNode[]) {
    nodes.sort((a, b) => a.label!.localeCompare(b.label!));
    nodes.forEach(node => {
      if (node.children && node.children.length) {
        sortTreeNodes(node.children);
      }
    });
  }

  export function findNodeById(nodes: TreeNode[] | null, key: string): TreeNode | null {
    if (!nodes) return null;

    for (const node of nodes) {
      if (String(node.key) === String(key)) return node;
      if (node.children?.length) {
        const found = findNodeById(node.children, key);
        if (found) return found;
      }
    }

    return null;
  }

  export function getFileExtensionFromMimeType(mimeType: string): string {
    const mimeToExtension: { [key: string]: string } = {
      'application/pdf': '.pdf',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
      'application/vnd.ms-excel': '.xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
      'application/vnd.ms-powerpoint': '.ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
      'text/plain': '.txt',
      'text/csv': '.csv',
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/bmp': '.bmp',
      'image/tiff': '.tiff',
      'application/zip': '.zip',
      'application/x-rar-compressed': '.rar',
      'application/x-7z-compressed': '.7z',
      'text/html': '.html',
      'application/xml': '.xml',
      'application/json': '.json'
    };

    return mimeToExtension[mimeType] || '.bin'; // Default to .bin for unknown types
  }

/**
 * The ErrorInterceptor rejects with a message array, a string, or an object. Runtime errors and
 * Angular's placeholder status texts ('OK', 'Unknown Error') carry no user-facing message, so the
 * translated fallback is used instead.
 */
function extractApiErrorMessages(err: any, translate: TranslateService, fallbackKey: string = 'form.error_saving_data'): string[] {
  for (const candidate of err instanceof Error ? [] : [err, err?.errors, err?.error?.errors, err?.error, err?.message]) {
    if (Array.isArray(candidate) && candidate.length) return candidate.map(String);
    if (typeof candidate === 'string' && candidate.trim() && !['OK', 'Unknown Error'].includes(candidate)) return [candidate];
  }
  return [translate.instant(fallbackKey)];
}

/** Shows one error toast per message of a failed API call. */
export function showApiErrors(
  messageService: MessageService | null,
  translate: TranslateService,
  err: any,
  fallbackKey: string = 'form.error_saving_data',
  toastKey?: string,
): void {
  if (!messageService) return;
  const summary = translate.instant('form.error');
  extractApiErrorMessages(err, translate, fallbackKey).forEach(detail =>
    messageService.add({key: toastKey, severity: 'error', summary, detail}),
  );
}

/** Blob requests reject with the error body as a Blob; reads it for the backend's message. */
export async function showBlobApiErrors(
  messageService: MessageService | null,
  translate: TranslateService,
  err: any,
  fallbackKey: string = 'default.done_failed',
): Promise<void> {
  let parsed: any = err;
  if (err instanceof Blob) {
    try {
      parsed = JSON.parse(await err.text());
    } catch {
      parsed = null;
    }
  }
  showApiErrors(messageService, translate, parsed, fallbackKey);
}

const lastApiErrorToastAt = new Map<string, number>();

/**
 * Like showApiErrors, but at most once per `key` within `windowMs`. Several instances of one selector
 * share the same cached request, so its failure is announced once.
 */
export function showApiErrorsOnce(
  key: string,
  messageService: MessageService | null,
  translate: TranslateService,
  err: any,
  fallbackKey: string = 'errors.messages.loading_failed',
  windowMs: number = 2000,
): void {
  const now = Date.now();
  if (!messageService || now - (lastApiErrorToastAt.get(key) ?? 0) < windowMs) return;
  lastApiErrorToastAt.set(key, now);
  showApiErrors(messageService, translate, err, fallbackKey);
}

/** The name of the option whose id matches, or '' (ids are compared as strings). */
export function findOptionName(options: { id: any; name: string }[] | null | undefined, id: any): string {
  if (id === null || id === undefined) return '';
  return options?.find(option => String(option.id) === String(id))?.name || '';
}

/** Text colour for a check/event status, matching the legacy `.status--*` classes. */
export function getStatusColorClass(status: string | null | undefined): string {
  switch (status) {
    case 'ok':
    case 'done':
      return 'tw-text-green';
    case 'workable':
    case 'not_ok':
      return 'tw-text-orange';
    case 'broken':
    case 'rejected':
      return 'tw-text-red';
    default:
      return '';
  }
}

/** Sets a table filter; with `index` the filter holds a list (e.g. interval + type) and only that entry changes. */
export function setFilterValue(filters: Record<string, unknown>, name: string, value: unknown, index?: number): void {
  if (index === undefined) {
    filters[name] = value;
    return;
  }
  const list = Array.isArray(filters[name]) ? filters[name] as unknown[] : [];
  list[index] = value;
  filters[name] = list;
}

/** Opens a file served by the API; relative urls are prefixed with the API host. */
export function openFileUrl(url: string | null | undefined, apiUrl: string): void {
  if (!url) return;
  window.open(/^https?:\/\//i.test(url) ? url : apiUrl + url);
}
